import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CalendarSyncModal from '../components/CalendarSyncModal';
import PhoneInput from '../components/PhoneInput';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, User, Mail, Phone, Lock, Eye, EyeOff, CheckCircle, Shield, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfileSettings = () => {
  const { user, token, loginUser, runningUser, isRunAsMode, getAuthHeaders, isCustomer, isCalendarAdmin } = useAuth();
  const navigate = useNavigate();
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Calendar Sync Modal state (Jan 15, 2026)
  const [showCalendarSync, setShowCalendarSync] = useState(false);
  
  // Profile loading state
  const [loadingProfile, setLoadingProfile] = useState(true);
  
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    phone: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Build headers for API calls - use getAuthHeaders from context
  const buildHeaders = () => getAuthHeaders();

  // Fetch profile data - in new architecture, runningUser is always the correct user
  useEffect(() => {
    const fetchProfile = async () => {
      setLoadingProfile(true);
      
      if (runningUser) {
        // runningUser always contains the correct user context
        setProfileData({
          full_name: runningUser.full_name || '',
          email: runningUser.email || '',
          phone: runningUser.phone || ''
        });
      }
      
      setLoadingProfile(false);
    };
    
    if (token) {
      fetchProfile();
    }
  }, [token, runningUser]);

  // In new architecture, runningUser is always the display user
  const displayUser = runningUser;

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: value
    });
    setProfileError('');
    setProfileSuccess('');
  };

  const handlePhoneChange = (value) => {
    setProfileData({
      ...profileData,
      phone: value
    });
    setProfileError('');
    setProfileSuccess('');
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value
    });
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    setSavingProfile(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/me/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...buildHeaders()
        },
        body: JSON.stringify({
          full_name: profileData.full_name,
          phone: profileData.phone || null
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setProfileSuccess('Profile updated successfully!');
      } else {
        setProfileError(data.detail || 'Failed to update profile');
      }
    } catch (err) {
      setProfileError('Failed to update profile. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validate password fields
    if (!passwordData.currentPassword) {
      setPasswordError('Please enter your current password');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setSavingPassword(true);

    try {
      const response = await fetch(
        `${API_URL}/api/auth/me/password?current_password=${encodeURIComponent(passwordData.currentPassword)}&new_password=${encodeURIComponent(passwordData.newPassword)}`,
        {
          method: 'PUT',
          headers: buildHeaders()
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setPasswordSuccess('Password changed successfully!');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setPasswordError(data.detail || 'Failed to change password');
      }
    } catch (err) {
      setPasswordError('Failed to change password. Please try again.');
    } finally {
      setSavingPassword(false);
    }
  };

  // Show loading while profile is being fetched
  if (loadingProfile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <section className="py-12">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading profile...</p>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6">
          <div className="mb-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </button>
            <h1 className="text-3xl font-bold text-gray-800">Profile Settings</h1>
            <p className="text-gray-600 mt-2">Manage your account information and security</p>
          </div>

          {/* Profile Info Section */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                <User className="w-5 h-5 mr-2 text-yellow-500" />
                Personal Information
              </h2>
              
              {profileError && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                  {profileError}
                </div>
              )}

              {profileSuccess && (
                <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {profileSuccess}
                </div>
              )}

              <form onSubmit={handleProfileSubmit} className="space-y-6">
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 mr-2" />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={profileData.full_name}
                    onChange={handleProfileChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    data-testid="full-name-input"
                    required
                  />
                </div>

                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={profileData.email}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                    disabled
                  />
                  <p className="text-sm text-gray-500 mt-1">Email cannot be changed</p>
                </div>

                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 mr-2" />
                    Phone Number
                  </label>
                  <PhoneInput
                    value={profileData.phone}
                    onChange={handlePhoneChange}
                    className="w-full"
                    data-testid="phone-input"
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={savingProfile}
                    className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-300 text-white rounded-md transition-colors"
                    data-testid="save-profile-btn"
                  >
                    {savingProfile ? 'Saving...' : 'Save Profile'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Password Change Section */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
                <Shield className="w-5 h-5 mr-2 text-yellow-500" />
                Change Password
              </h2>
              <p className="text-gray-600 mb-6">Update your password to keep your account secure</p>
              
              {passwordError && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                  {passwordError}
                </div>
              )}

              {passwordSuccess && (
                <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {passwordSuccess}
                </div>
              )}

              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent pr-10"
                      placeholder="Enter your current password"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent pr-10"
                      placeholder="Minimum 6 characters"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent pr-10"
                      placeholder="Re-enter new password"
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    disabled={savingPassword}
                    className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-300 text-white rounded-md transition-colors"
                    data-testid="change-password-btn"
                  >
                    {savingPassword ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Account Info Section */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-8 bg-gray-50 rounded-lg">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Account Information</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Account Type:</span>
                  <span className={`font-semibold px-3 py-1 rounded-full text-sm ${
                    runningUser?.role === 'super_admin' 
                      ? 'bg-red-100 text-red-800'
                      : runningUser?.role === 'calendar_admin'
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-green-100 text-green-800'
                  }`}>
                    {runningUser?.role === 'super_admin' ? 'Super Admin' : 
                     runningUser?.role === 'calendar_admin' ? 'Calendar Admin' : 'Customer'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Email Verified:</span>
                  <span className={`font-semibold ${runningUser?.is_email_verified ? 'text-green-600' : 'text-orange-600'}`}>
                    {runningUser?.is_email_verified ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className="font-semibold text-green-600">Active</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Member Since:</span>
                  <span className="font-semibold text-gray-800">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { 
                      month: 'long', 
                      day: 'numeric', 
                      year: 'numeric' 
                    }) : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* CALENDAR SYNC SECTION (Jan 15, 2026):
              Moved from CalendarView to ProfileSettings for better organization
              Available for customers and calendar admins */}
          {(isCustomer() || isCalendarAdmin()) && (
            <div className="bg-white rounded-lg shadow mt-6">
              <div className="p-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-yellow-500" />
                  Calendar Integration
                </h2>
                <p className="text-gray-600 mb-6">
                  Sync your appointments to your personal calendar (Google Calendar, Apple Calendar, Outlook) 
                  to keep track of your bookings alongside your other events.
                </p>
                <button
                  onClick={() => setShowCalendarSync(true)}
                  className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors flex items-center gap-2"
                  data-testid="sync-calendar-btn"
                >
                  <Calendar className="w-5 h-5" />
                  Sync to Personal Calendar
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
      
      {/* Calendar Sync Modal (Jan 15, 2026) */}
      <CalendarSyncModal 
        isOpen={showCalendarSync} 
        onClose={() => setShowCalendarSync(false)}
      />
    </div>
  );
};

export default ProfileSettings;
